import { useEffect, useRef, type PointerEvent } from 'react'

const PERIOD = 1150
const COLS = 31
const ROWS = 17
const BOW = { pinch: 0.48, lower: 1.2, lift: 0.14, exp: 2.2, edge: 0.1, dual: false, dens: 0, glow: 3.2, soft: 0.08 }

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t
}
function smooth(a: number, b: number, x: number) {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)))
  return t * t * (3 - 2 * t)
}
function viseme(t: number) {
  const keys = {
    m: { rx: 0.93, ry: 0.16, thick: 0.72 },
    e: { rx: 0.9, ry: 0.38, thick: 0.42 },
    a: { rx: 0.94, ry: 0.72, thick: 0.36 },
    o: { rx: 0.58, ry: 0.64, thick: 0.4 },
  }
  const seq: [number, keyof typeof keys][] = [[0, 'm'], [0.1, 'm'], [0.24, 'a'], [0.4, 'o'], [0.58, 'e'], [0.76, 'a'], [1, 'm']]
  let a: [number, keyof typeof keys] = seq[0]!
  let b: [number, keyof typeof keys] = seq[1]!
  for (let i = 0; i < seq.length - 1; i++) {
    const cur = seq[i]
    const nxt = seq[i + 1]
    if (cur && nxt && t >= cur[0]) { a = cur; b = nxt }
  }
  const u = (t - a[0]) / (b[0] - a[0] || 1)
  const s = u * u * (3 - 2 * u)
  const A = keys[a[1]]
  const B = keys[b[1]]
  return { rx: lerp(A.rx, B.rx, s), ry: lerp(A.ry, B.ry, s), thick: lerp(A.thick, B.thick, s) }
}

export function TalkTtsOrb({
  label,
  onPointerDown,
  onPointerUp,
  onPointerCancel,
}: {
  label: string
  onPointerDown?: (e: PointerEvent<HTMLButtonElement>) => void
  onPointerUp?: () => void
  onPointerCancel?: () => void
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    let raf = 0
    const tick = (now: number) => {
      const t = (now % PERIOD) / PERIOD
      const v = viseme(t)
      const s = BOW
      const w = canvas.width, h = canvas.height
      ctx.clearRect(0, 0, w, h)
      const cw = w / COLS, ch = h / ROWS
      for (let y = 0; y < ROWS; y++) {
        for (let x = 0; x < COLS; x++) {
          const nx = (x + 0.5) / COLS * 2 - 1
          const ny = (y + 0.5) / ROWS * 2 - 1
          const yy = ny + s.lift * nx * nx
          const ry = v.ry * (yy > 0 ? s.lower : 0.88)
          const ax = Math.abs(nx) / v.rx
          const ay = Math.abs(yy) / Math.max(ry, 0.06)
          const pinch = 1 + s.pinch * ax * ax
          const e = Math.pow(ax, s.exp) + Math.pow(ay * pinch, s.exp)
          const inner = Math.max(0.08, 1 - v.thick)
          const aOut = smooth(1 + s.soft, 1 - s.soft, e)
          const aIn = smooth(inner - s.soft, inner + s.soft, e)
          const a = aOut * aIn
          if (a < 0.06) continue
          ctx.globalAlpha = a
          ctx.fillStyle = 'rgb(125,232,249)'
          ctx.shadowColor = '#22d3ee'
          ctx.shadowBlur = s.glow
          ctx.beginPath()
          ctx.arc((x + 0.5) * cw, (y + 0.5) * ch, Math.min(cw, ch) * 0.28, 0, Math.PI * 2)
          ctx.fill()
        }
      }
      ctx.globalAlpha = 1
      ctx.shadowBlur = 0
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [])
  const dots = [0, 45, 90, 135, 180, 225, 270, 315]
  return (
    <button
      type="button"
      className="talk-orb-field hp"
      aria-label={label}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
    >
      <div className="talk-halo" />
      <i className="talk-spark" style={{ ['--a' as string]: '20deg' }} />
      <i className="talk-spark" style={{ ['--a' as string]: '140deg' }} />
      <i className="talk-spark" style={{ ['--a' as string]: '250deg' }} />
      <div className="vu" aria-hidden>
        {dots.map(a => <i key={a} style={{ ['--a' as string]: `${a}deg` }} />)}
      </div>
      <div className="talk-ring r1" />
      <div className="talk-ring r2" />
      <div className="talk-ring r3" />
      <span className="talk-orb">
        <span className="tts-ripple" />
        <span className="tts-ripple b" />
        <span className="talk-orb-core">
          <div className="gmouth"><canvas ref={canvasRef} width={184} height={96} /></div>
        </span>
      </span>
    </button>
  )
}
